import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiKey, FiCopy, FiX, FiAlertTriangle } from 'react-icons/fi';
import { usersAPI } from '../services/api';

// Chairman-only offboarding tool: force-rotate any staff member's login
// credentials (username/password) and revoke their standing session — e.g.
// when someone leaves the company and a new person is taking over their
// role's account. Deliberately excludes STUDENT rows: this is for staff/
// leadership accounts, mirrors the backend's reset-credentials rejection.
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [target, setTarget] = useState(null); // user row currently being reset
  const [result, setResult] = useState(null); // { username, new_password } — shown once

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // /api/users/ is paginated (PAGE_SIZE=50) and most rows are STUDENT —
      // unlike other usersAPI.list() callers (Calendar, AccessGrants,
      // StudentAppAdmin), which only need page 1 for a dropdown, this page's
      // whole job is finding *any* staff account, so silently stopping at
      // page 1 would hide real accounts. Page through everything.
      let all = [];
      let response = await usersAPI.list();
      all = all.concat(response.data.results || response.data || []);
      while (response.data.next) {
        response = await usersAPI.list({ page: new URL(response.data.next).searchParams.get('page') });
        all = all.concat(response.data.results || []);
      }
      setUsers(all.filter((u) => u.role !== 'STUDENT'));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading users...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <FiKey size={22} color="#4facfe" />
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>User Credentials</h1>
      </div>
      <p style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>
        Reset any staff member's login credentials — for example when someone leaves the
        company and a new person is taking over their role's account. Resetting immediately
        revokes their current session, so the old credentials stop working right away.
      </p>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, background: 'rgba(245, 87, 87, 0.12)', color: '#e04b4b', border: '1px solid rgba(245, 87, 87, 0.3)' }}>
          {error}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
              <th style={{ padding: '10px 14px' }}>Username</th>
              <th style={{ padding: '10px 14px' }}>Name</th>
              <th style={{ padding: '10px 14px' }}>Email</th>
              <th style={{ padding: '10px 14px' }}>Role</th>
              <th style={{ padding: '10px 14px' }}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: '10px 14px' }}>@{u.username}</td>
                <td style={{ padding: '10px 14px' }}>{u.first_name} {u.last_name}</td>
                <td style={{ padding: '10px 14px' }}>{u.email}</td>
                <td style={{ padding: '10px 14px' }}>{u.role}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                  <button
                    onClick={() => setTarget(u)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                      background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151',
                    }}
                  >
                    <FiKey size={12} /> Reset Credentials
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {target && (
        <ResetCredentialsModal
          user={target}
          onClose={() => setTarget(null)}
          onDone={(res) => { setTarget(null); setResult(res); }}
        />
      )}

      {result && (
        <ResultModal result={result} onClose={() => setResult(null)} />
      )}
    </div>
  );
};

const modalOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};

const ResetCredentialsModal = ({ user, onClose, onDone }) => {
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      if (newUsername.trim()) payload.new_username = newUsername.trim();
      if (newPassword) payload.new_password = newPassword;
      const { data } = await usersAPI.resetCredentials(user.id, payload);
      onDone(data);
    } catch (e2) {
      const detail = e2?.response?.data?.error;
      setError(Array.isArray(detail) ? detail.join(' ') : (detail || 'Failed to reset credentials.'));
    } finally {
      setSaving(false);
    }
  };

  // Rendered via a portal straight onto <body> — .content-wrapper's
  // backdrop-filter (Layout.css) creates a new containing block for any
  // position:fixed descendant, so nested here this modal would center
  // itself against the *whole scrollable table*, not the viewport, and
  // silently scroll out of view. A portal escapes that ancestor entirely.
  return createPortal((
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Reset credentials</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#888' }}><FiX /></button>
        </div>
        <p style={{ fontSize: 13, color: '#666', marginTop: 8, marginBottom: 16 }}>
          @{user.username} ({user.role}). Their current session will be revoked immediately.
        </p>

        {error && (
          <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 12, background: 'rgba(245, 87, 87, 0.12)', color: '#e04b4b' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>New username (optional — leave blank to keep @{user.username})</span>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder={user.username}
              style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>New password (optional — leave blank to auto-generate a strong one)</span>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Auto-generate"
              style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 8, border: 'none',
              background: saving ? '#f0a9a9' : '#e04b4b', color: '#fff',
              fontWeight: 600, fontSize: 14, cursor: saving ? 'default' : 'pointer',
            }}
          >
            {saving ? 'Resetting...' : 'Reset credentials'}
          </button>
        </form>
      </div>
    </div>
  ), document.body);
};

const ResultModal = ({ result, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(result.new_password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return createPortal((
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Credentials reset</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, background: 'rgba(255, 193, 7, 0.12)', border: '1px solid rgba(255, 193, 7, 0.35)', marginBottom: 16, fontSize: 12, color: '#8a6d00' }}>
          <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>This password is shown once and cannot be retrieved later. Hand it to its owner out-of-band — not over chat or email.</span>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Username</span>
          <div style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#f9fafb' }}>
            @{result.username}
          </div>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>New password</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#f9fafb', fontFamily: 'monospace' }}>
              {result.new_password}
            </div>
            <button
              onClick={handleCopy}
              title="Copy"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12 }}
            >
              <FiCopy size={13} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </label>

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '10px 18px', borderRadius: 8, border: 'none',
            background: '#4facfe', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </div>
  ), document.body);
};

export default UserManagement;
